<template>
  <div>
    <div class="d-flex flex-column flex-xl-row justify-content-between">
      <!-- Date Filter -->
      <div class="custom-date-picker mx-1 mb-2">
        <home-date-picker :from-date="filters.fromDate" :to-date="filters.toDate" :emit="false" :date-range="dateRange"
          @dates-changed="updateDates" />
      </div>

      <!-- Priority Filter -->
      <div class="dropdown square mx-1 mb-2">
        <div class="drop-select" data-toggle="dropdown">
          <span :class="{ active: filters.priority !== null }">{{ priorityLabel }}</span>
          <i class="fas fa-chevron-down dropdown-icon"></i>
        </div>
        <div class="dropdown-menu">
          <a class="dropdown-item" :class="{ active: filters.priority === '' }" @click="setFilter('priority', null)">{{ __('All Priorities') }}</a>
          <a class="dropdown-item" :class="{ active: filters.priority === '0' }" @click="setFilter('priority', '0')">{{ __('Low') }}</a>
          <a class="dropdown-item" :class="{ active: filters.priority === '1' }" @click="setFilter('priority', '1')">{{ __('Medium') }}</a>
          <a class="dropdown-item" :class="{ active: filters.priority === '2' }" @click="setFilter('priority', '2')">{{ __('High') }}</a>
        </div>
      </div>

      <!-- Status Filter -->
      <div class="dropdown square mx-1 mb-2">
        <div class="drop-select" data-toggle="dropdown">
          <span :class="{ active: filters.status !== null }">{{ statusLabel }}</span>
          <i class="fas fa-chevron-down dropdown-icon"></i>
        </div>
        <div class="dropdown-menu">
          <a class="dropdown-item" :class="{ active: filters.status === '' }" @click="setFilter('status', null)">{{ __('All Statuses') }}</a>
          <a class="dropdown-item" :class="{ active: filters.status === 'new' }" @click="setFilter('status', 'new')">{{ __('New') }}</a>
          <a class="dropdown-item" :class="{ active: filters.status === 'open' }" @click="setFilter('status', 'open')">{{ __('Open') }}</a>
          <a class="dropdown-item" :class="{ active: filters.status === 'closed' }" @click="setFilter('status', 'closed')">{{ __('Closed') }}</a>
        </div>
      </div>

      <!-- Type Filter -->
      <div class="dropdown square mx-1 mb-2">
        <div class="drop-select" data-toggle="dropdown">
          <span :class="{ active: filters.type !== null }">{{ typeLabel }}</span>
          <i class="fas fa-chevron-down dropdown-icon"></i>
        </div>
        <div class="dropdown-menu">
          <a class="dropdown-item" :class="{ active: filters.type === '' }" @click="setFilter('type', null)">{{ __('All Types') }}</a>
          <a class="dropdown-item" :class="{ active: filters.type === 'task' }" @click="setFilter('type', 'task')">{{ __('Task') }}</a>
          <a class="dropdown-item" :class="{ active: filters.type === 'issue' }" @click="setFilter('type', 'issue')">{{ __('Issue') }}</a>
          <a class="dropdown-item" :class="{ active: filters.type === 'request' }" @click="setFilter('type', 'request')">{{ __('Request') }}</a>
        </div>
      </div>

      <!-- Case Filter -->
      <div class="dropdown square mx-1 mb-2">
        <div class="drop-select" data-toggle="dropdown">
          <span :class="{ active: filters.caseDetails !== null }">{{ caseLabel }}</span>
          <i class="fas fa-chevron-down dropdown-icon"></i>
        </div>
        <div class="dropdown-menu">
          <a class="dropdown-item" :class="{ active: filters.caseDetails === '' }" @click="setFilter('caseDetails', null)">{{ __('All Cases') }}</a>
          <a class="dropdown-item" v-if="!isGuestUser()" :class="{ active: filters.caseDetails === 'assignedCases' }" @click="setFilter('caseDetails', 'assignedCases')">{{ __('Assigned to me') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withComments' }" @click="setFilter('caseDetails', 'withComments')">{{ __('With Comments') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withoutComments' }" @click="setFilter('caseDetails', 'withoutComments')">{{ __('Without Comments') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withAssignees' }" @click="setFilter('caseDetails', 'withAssignees')">{{ __('With Assignees') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withoutAssignees' }" @click="setFilter('caseDetails', 'withoutAssignees')">{{ __('Without Assignees') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withReviews' }" @click="setFilter('caseDetails', 'withReviews')">{{ __('With Reviews') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withoutReviews' }" @click="setFilter('caseDetails', 'withoutReviews')">{{ __('Without Reviews') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withSurveys' }" @click="setFilter('caseDetails', 'withSurveys')">{{ __('With Surveys') }}</a>
          <a class="dropdown-item" :class="{ active: filters.caseDetails === 'withoutSurveys' }" @click="setFilter('caseDetails', 'withoutSurveys')">{{ __('Without Surveys') }}</a>
        </div>
      </div>

      <!-- Department Filter -->
      <div class="dropdown square mx-1 mb-2" v-if="availableDepartments.length">
        <div class="drop-select" data-toggle="dropdown">
          <span :class="{ active: filters.departmentId !== null }">{{ departmentLabel }}</span>
          <i class="fas fa-chevron-down dropdown-icon"></i>
        </div>
        <div class="dropdown-menu">
          <a class="dropdown-item" :class="{ active: filters.department === null }" @click="setFilter('departmentId', null)">{{ __('All Departments') }}</a>
          <a
            v-for="department in availableDepartments"
            :key="department.id"
            class="dropdown-item"
            :class="{ active: filters.department === department.id }"
            @click="setFilter('departmentId', department.id)"
          >
            {{ labelizeGeneral(department.name) }}
          </a>
          <!-- Add more departments as needed -->
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CaseService from '@/services/cases-service';

export default {
  data() {
    return {
      dateRange: __('Year To Date'),
      availableDepartments: [],
      filters: {
        fromDate: moment().startOf('year').format('YYYY-MM-DD'),
        toDate: moment().format('YYYY-MM-DD'),
        priority: null,
        status: null,
        type: null,
        caseDetails: null,
        departmentId: null,
      },
    };
  },

  computed: {
    priorityLabel() {
      switch (this.filters.priority) {
        case '0':
          return __('Low');
        case '1':
          return __('Medium');
        case '2':
          return __('High');
        default:
          return __('All Priorities');
      }
    },

    statusLabel() {
      switch (this.filters.status) {
        case 'new':
          return __('New');
        case 'open':
          return __('Open');
        case 'closed':
          return __('Closed');
        default:
          return __('All Statuses');
      }
    },

    typeLabel() {
      switch (this.filters.type) {
        case 'task':
          return __('Task');
        case 'issue':
          return __('Issue');
        case 'request':
          return __('Request');
        default:
          return __('All Types');
      }
    },

    caseLabel() {
      switch (this.filters.caseDetails) {
        case 'withComments':
          return __('With Comments');
        case 'withoutComments':
          return __('Without Comments');
        case 'withAssignees':
          return __('With Assignees');
        case 'withoutAssignees':
          return __('Without Assignees');
        case 'withReviews':
          return __('With Reviews');
        case 'withoutReviews':
          return __('Without Reviews');
        case 'withSurveys':
          return __('With Surveys');
        case 'withoutSurveys':
          return __('Without Surveys');
        case 'assignedCases':
          return __('Assigned to me');
        default:
          return __('All Cases');
      }
    },

    departmentLabel() {
      return this.filters.department
        ? this.availableDepartments.find(department => department.id === this.filters.department)?.name || __('All Departments')
        : __('All Departments');
    },
  },

  mounted() {
    // Fetch available departments
    this.getAvailableDepartments();
    // Emit initial filters to cases
    this.emitFilters();
  },

  methods: {
    isGuestUser() {
      return !Spark?.userId;
    },

    updateDates(newFromDate, newToDate, dateRangeType) {
      this.filters.fromDate = newFromDate;
      this.filters.toDate = newToDate;

      if (dateRangeType) {
        this.dateRange = dateRangeType;
      } else if (
        moment(newFromDate).isSame(moment().startOf('year'), 'day') &&
        moment(newToDate).isSame(moment(), 'day')
      ) {
        this.dateRange = __('Year To Date');
      } else {
        this.dateRange = `${moment(newFromDate).format('D MMM YYYY')} - ${moment(newToDate).format('D MMM YYYY')}`;
      }

      this.emitFilters();
    },

    setFilter(key, value) {
      if (Object.prototype.hasOwnProperty.call(this.filters, key)) {
        this.filters[key] = value;
      } else {
        console.warn(`Key "${key}" does not exist in filters.`);
      }

      this.emitFilters();
    },

    emitFilters() {
      this.$emit('filters-updated', { ...this.filters });
    },

    async getAvailableDepartments() {
      this.availableDepartments = (await CaseService.getDepartments(Spark.state.currentTeam.id))?.departments
    },
  },
};
</script>

<style scoped>
.dropdown.square {
  display: flex!important;
  flex-grow: 1!important;
}

.custom-date-picker ::v-deep(.dropdown-select) {
  display: flex;
  padding-top: 10px;
  padding-right: 30px;
  padding-bottom: 10px;
  padding-left: 15px;
  border: 1px solid #ddd;
  cursor: pointer;
  min-width: 100px;

  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}

.drop-select {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background-color: #fff;
  border: 1px solid #ddd;
  cursor: pointer;
  min-width: 120px;
  font-size: 11px;
  flex-grow: 1!important;
}

.dropdown-icon {
  font-size: 8px;
}

span.active {
  color: #00aef1;
}
</style>
